import { FC, HTMLProps, Ref, useImperativeHandle, useRef, useState } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ProductVariant } from '@telialt/models/src/EshopModel';
import { scrollElementIntoPageView } from '@telialt/utils/src/helpers';
import { useNavigationOffset } from '@telialt/utils/src/NavigationUtils/useNavigationOffset';

import { ProductRetails } from '../ProductRetails/ProductRetails';
import { ProductRetailsPlaceholder } from '../ProductRetails/ProductRetailsPlaceholder';
import { TechnicalCharacteristicsAlignment } from '../TechnicalCharacteristics/model';
import { TechnicalCharacteristics } from '../TechnicalCharacteristics/TechnicalCharacteristics';
import { ProductDescription, ProductOverviewTitle } from './ProductOverview.styles';
import { ShowMoreOrLess } from './ShowMoreOrLess/ShowMoreOrLess';

export type ProductOverviewHandle = {
    showRetails: () => void;
};

type Props = {
    productVariant: ProductVariant;
    isRetailsShown?: boolean;
    technicalCharacteristicsAlignment?: TechnicalCharacteristicsAlignment;
    ref?: Ref<ProductOverviewHandle & HTMLElement>;
} & HTMLProps<HTMLElement>;

export const ProductOverview: FC<Props> = ({
    productVariant,
    isRetailsShown = true,
    technicalCharacteristicsAlignment,
    ref,
    ...htmlProps
}) => {
    const { t } = useTranslations();

    const navigationTopOffset = useNavigationOffset();

    const showRetails = () => {
        const retailsElement = document.getElementById('retails-map-title');

        if (!retailsElement || !isRetailsShown) {
            return;
        }

        window.setTimeout(() => {
            scrollElementIntoPageView(retailsElement, 'smooth', 'top', -navigationTopOffset);
        }, 100);
    };

    useImperativeHandle<ProductOverviewHandle, ProductOverviewHandle>(ref, () => ({
        showRetails,
    }));

    const descriptionTitleRef = useRef(null);
    const characteristicsTitleRef = useRef(null);
    const [wasShowMoreClicked, setWasShowMoreClicked] = useState(false);

    return (
        <section {...htmlProps}>
            {productVariant?.description && (
                <>
                    <ProductOverviewTitle ref={descriptionTitleRef}>
                        {t['eshop.product_page.overview.title']}
                    </ProductOverviewTitle>
                    <ShowMoreOrLess titleRef={descriptionTitleRef}>
                        <ProductDescription content={productVariant.description} />
                    </ShowMoreOrLess>
                </>
            )}
            {productVariant?.classifications && (
                <>
                    <ProductOverviewTitle ref={characteristicsTitleRef}>
                        {t['eshop.product_page.technical_characteristics.title']}
                    </ProductOverviewTitle>
                    <ShowMoreOrLess titleRef={characteristicsTitleRef}>
                        <TechnicalCharacteristics
                            classifications={productVariant.classifications}
                            alignment={technicalCharacteristicsAlignment}
                        />
                    </ShowMoreOrLess>
                </>
            )}
            {isRetailsShown && (
                <>
                    <ProductOverviewTitle id="retails-map-title">
                        {t['eshop.product_page.retails_map.title']}
                    </ProductOverviewTitle>
                    {wasShowMoreClicked ? (
                        <ProductRetails />
                    ) : (
                        <ShowMoreOrLess
                            onExpanded={() => setWasShowMoreClicked(true)}
                            minHeightToShowExpand={1} // we should always have the ShowMore button visible
                        >
                            <ProductRetailsPlaceholder />
                        </ShowMoreOrLess>
                    )}
                </>
            )}
        </section>
    );
};
